import { createSelector } from "@reduxjs/toolkit";
import { selectNetwork } from "state/app/selectors";
import { RootState } from "state/store";
import { FetchStatus } from "state/types";

export const selectMarkets = createSelector(
	selectNetwork,
	(state: RootState) => state.exchange.markets,
	(network, markets) => {
		return markets[network] ?? []
	}
)

export const selectMarketVolumes = createSelector(
	selectNetwork,
	(state: RootState) => state.exchange.dailyMarketVolumes,
	(network, dailyMarketVolumes) => {
		return dailyMarketVolumes[network] ?? []
	}
)

export const selectTokens = createSelector(
	selectNetwork,
	(state: RootState) => state.exchange.tokensMap,
	(network, tokens) => {
		return tokens[network] ?? []
	}
)

export const selectOpenOrders = createSelector(
	selectNetwork,
	(state: RootState) => state.exchange.openOrders,
	(network, openOrders) => {
		return openOrders[network] ?? []
	}
)

export const selectCurrentMarketAsset = (state: RootState) => state.exchange.selectedMarketAsset

export const selectOrderbook = createSelector(
	selectCurrentMarketAsset,
	(state: RootState) => state.exchange.orderbooks,
	(market, orderbook) => {
		return orderbook[market] ?? null
	}
)

export const selectLiquidityBook = createSelector(
	selectCurrentMarketAsset,
	(state: RootState) => state.exchange.liquidityBooks,
	(market, liquidityBooks) => {
		return liquidityBooks[market] ?? null
	}
)

export const selectCurrentMarketInfo = createSelector(
	selectMarkets,
	selectCurrentMarketAsset,
	(markets, pairAddress) => {
		return markets.find(e=>e.marketAddress == pairAddress)
	}
)

export const selectOrderbookQueryStatus = (state: RootState) => state.exchange.queryStatuses.orderbook

export const selectLiquidityBookQueryStatus = (state: RootState) => state.exchange.queryStatuses.liquidityBook

export const selectOpenOrdersQueryStatus = (state: RootState) => state.exchange.queryStatuses.openOrders

export const selectUserLiquiditiesQueryStatus = (state: RootState) => state.exchange.queryStatuses.userLiquidities

export const selectUserLiquidities = createSelector(
	selectCurrentMarketAsset,
	(state: RootState) => state.exchange.userLiquidities,
	(market, userLiquidities) => {
		return userLiquidities[market] ?? []
	}
)

export const selectOrderType = (state: RootState) => state.exchange.orderType

export const selectOrderDirection = (state: RootState) => state.exchange.orderDirection

export const selectMarketsQueryStatus = (state: RootState) => state.exchange.queryStatuses.markets

export const selectOrderPrice = (state: RootState) => state.exchange.orderPrice

export const selectOrderSize = (state: RootState) => state.exchange.orderSize

export const selectOrderTotal = (state: RootState) => state.exchange.orderTotal

export const selectSlippage = (state: RootState) => state.exchange.slippage

export const selectDesireMode = (state: RootState) => state.exchange.desireMode

export const selectMakeOrderFinished = (state: RootState) => state.exchange.writeStatuses.makeOrder !== FetchStatus.Loading

export const selectPlaceLiquidityFinished = (state: RootState) => state.exchange.writeStatuses.placeLiquidity !== FetchStatus.Loading

export const selectOrderbookWidth = (state: RootState) => state.exchange.orderbookWidth

export const selectClaimEarningStatus = (state: RootState) => state.exchange.writeStatuses.claimEarning

export const selectCancelOrderStatus = (state: RootState) => state.exchange.writeStatuses.cancelOrder

export const selectCollectLiquidityStatus = (state: RootState) => state.exchange.writeStatuses.collectLiquidity

export const selectBurnLiquidityStatus = (state: RootState) => state.exchange.writeStatuses.burnLiquidity

export const selectListTokenStatus = (state: RootState) => state.exchange.writeStatuses.listToken

export const selectListPairStatus = (state: RootState) => state.exchange.writeStatuses.listPair

export const selectQuickSwapBaseToken = (state: RootState) => state.exchange.quickSwapInfo.baseToken

export const selectQuickSwapQuoteToken = (state: RootState) => state.exchange.quickSwapInfo.quoteToken

export const selectOrderErrorMessage = (state: RootState) => state.exchange.orderErrorMessage

export const selectLiq2AmountRange = createSelector(
	selectCurrentMarketAsset,
	(state: RootState) => state.exchange.liq2AmountRanges,
	(market, liq2AmountRanges) => {
		return liq2AmountRanges[market] ?? null
	}
)

export const selectLiq2AmountRangeQueryStatus = (state: RootState) => state.exchange.queryStatuses.liq2AmountRange
